import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getCourseById, enrollInCourse } from '../../api/educentialApi';
import {
  BookOpen,
  Clock,
  Users,
  User,
  Award,
  ChevronDown,
  ChevronRight,
  Play,
  FileText,
  CheckCircle,
  ArrowLeft,
  Calendar,
  TrendingUp,
  Target,
  Sparkles,
  AlertTriangle
} from 'lucide-react';
import './CourseDetail.css';

function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [expandedModules, setExpandedModules] = useState([]);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    loadCourseDetails();
  }, [id]);

  const loadCourseDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCourseById(id);
      if (response.success) {
        setCourse(response.data);
        // Expand first module by default
        if (response.data.Modules?.length > 0) {
          setExpandedModules([response.data.Modules[0].id]);
        }
      }
    } catch (err) {
      console.error('Failed to load course:', err);
      setError(err.message || 'Failed to load course details');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    // Check if user is logged in
    if (!user) {
      setNotification({
        type: 'error',
        message: 'Please login to enroll in this course'
      });
      setTimeout(() => {
        navigate('/login', { state: { from: `/courses/${course.id}` } });
      }, 1500);
      return;
    }

    try {
      setEnrolling(true);
      const response = await enrollInCourse(course.id);
      if (response.success) {
        setNotification({
          type: 'success',
          message: `Successfully enrolled in "${course.title}"!`
        });
        setTimeout(() => {
          navigate(`/courses/${course.id}/learn`);
        }, 1000);
      }
    } catch (err) {
      console.error('Enrollment error:', err);
      if (err.message.includes('Already enrolled')) {
        setNotification({
          type: 'info',
          message: 'You are already enrolled in this course'
        });
        setTimeout(() => {
          navigate(`/courses/${course.id}/learn`);
        }, 1500);
      } else if (err.response?.status === 401) {
        setNotification({
          type: 'error',
          message: 'Please login to enroll in courses'
        });
        setTimeout(() => {
          navigate('/login', { state: { from: `/courses/${course.id}` } });
        }, 1500);
      } else {
        setNotification({
          type: 'error',
          message: err.message || 'Failed to enroll in course. Please try again.'
        });
      }
    } finally {
      setEnrolling(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const calculateTotalLessons = () => {
    return course?.Modules?.reduce((sum, module) => sum + (module.Lessons?.length || 0), 0) || 0;
  };

  const getLessonIcon = (lessonType) => {
    switch (lessonType) {
      case 'VIDEO': return Play;
      case 'ARTICLE': return FileText;
      case 'DOCUMENT': return FileText;
      case 'QUIZ': return CheckCircle;
      case 'ASSIGNMENT': return Award;
      default: return BookOpen;
    }
  };

  if (loading) {
    return (
      <div className="course-detail-container">
        <div className="loading-state">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="course-detail-container">
        <div className="error-state">
          <div className="error-icon">
            <AlertTriangle className="w-16 h-16 text-red-500" />
          </div>
          <h2>Failed to Load Course</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button className="btn-secondary" onClick={() => navigate('/courses/catalog')}>
              Back to Catalog
            </button>
            <button className="btn-primary" onClick={loadCourseDetails}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalLessons = calculateTotalLessons();
  const enrollmentCount = course._count?.Enrollments || 0;

  return (
    <div className="course-detail-container">
      {/* Notification Banner */}
      {notification && (
        <div className={`notification-banner ${notification.type}`}>
          <div className="notification-content">
            {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {notification.type === 'error' && <AlertTriangle className="w-5 h-5" />}
            {notification.type === 'info' && <BookOpen className="w-5 h-5" />}
            <span>{notification.message}</span>
          </div>
          <button onClick={() => setNotification(null)} className="notification-close">
            ×
          </button>
        </div>
      )}

      {/* Back Button */}
      <div className="back-navigation">
        <button onClick={() => navigate('/courses/catalog')} className="btn-back">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Courses</span>
        </button>
      </div>

      {/* Course Header */}
      <div className="course-detail-header">
        <div className="header-content">
          <div className="header-badges">
            <span className="badge badge-category">{course.category}</span>
            <span className="badge badge-level">{course.level}</span>
            {course.term && <span className="badge badge-term">{course.term}</span>}
          </div>

          <h1 className="course-title">{course.title}</h1>
          <p className="course-description">{course.description}</p>

          <div className="course-meta-stats">
            <div className="meta-stat">
              <User className="w-5 h-5 text-teal-600" />
              <span>{course.Creator?.fullName || 'Educator'}</span>
            </div>
            <div className="meta-stat">
              <Users className="w-5 h-5 text-teal-600" />
              <span>{enrollmentCount} {enrollmentCount === 1 ? 'student' : 'students'}</span>
            </div>
            <div className="meta-stat">
              <BookOpen className="w-5 h-5 text-teal-600" />
              <span>{totalLessons} lessons</span>
            </div>
            <div className="meta-stat">
              <Clock className="w-5 h-5 text-teal-600" />
              <span>{course.duration} hours</span>
            </div>
            {course.createdAt && (
              <div className="meta-stat">
                <Calendar className="w-5 h-5 text-teal-600" />
                <span>Created {new Date(course.createdAt).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>

        <div className="header-sidebar">
          <div className="enrollment-card">
            <div className="enrollment-card-header">
              {course.thumbnail ? (
                <img src={course.thumbnail} alt={course.title} className="course-preview-img" />
              ) : (
                <div className="course-preview-placeholder">
                  <Sparkles className="w-16 h-16 text-white" />
                </div>
              )}
            </div>
            <div className="enrollment-card-body">
              <div className="price-section">
                {course.price && parseFloat(course.price) > 0 ? (
                  <div className="price">₦{parseFloat(course.price).toLocaleString()}</div>
                ) : (
                  <div className="price-free">Free</div>
                )}
              </div>

              <button
                className="btn-enroll-large"
                onClick={handleEnroll}
                disabled={enrolling}
              >
                {enrolling ? 'Enrolling...' : 'Enroll in Course'}
              </button>

              <div className="enrollment-features">
                <div className="feature-item">
                  <CheckCircle className="w-5 h-5 text-teal-600" />
                  <span>Full lifetime access</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="w-5 h-5 text-teal-600" />
                  <span>Access on mobile and desktop</span>
                </div>
                <div className="feature-item">
                  <CheckCircle className="w-5 h-5 text-teal-600" />
                  <span>Certificate of completion</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What You'll Learn Section */}
      <div className="course-content-section">
        <div className="section-main">
          {/* Course Curriculum */}
          <div className="curriculum-section">
            <div className="section-header">
              <h2>Course Curriculum</h2>
              <p className="section-subtitle">
                {course.Modules?.length || 0} modules • {totalLessons} lessons • {course.duration}h total length
              </p>
            </div>

            <div className="modules-list">
              {course.Modules?.map((module, index) => {
                const isExpanded = expandedModules.includes(module.id);
                const lessonCount = module.Lessons?.length || 0;

                return (
                  <div key={module.id} className="module-accordion">
                    <div
                      className="module-accordion-header"
                      onClick={() => toggleModule(module.id)}
                    >
                      <div className="module-header-left">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-gray-600" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-600" />
                        )}
                        <div className="module-info">
                          <div className="module-number">Module {index + 1}</div>
                          <h3 className="module-title">{module.title}</h3>
                        </div>
                      </div>
                      <div className="module-stats">
                        <span className="lesson-count">{lessonCount} lessons</span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="module-accordion-content">
                        {module.description && (
                          <p className="module-description">{module.description}</p>
                        )}
                        <div className="lessons-list">
                          {module.Lessons?.map((lesson, lessonIndex) => {
                            const LessonIcon = getLessonIcon(lesson.type);
                            return (
                              <div key={lesson.id} className="lesson-item">
                                <div className="lesson-icon-wrapper">
                                  <LessonIcon className="w-4 h-4 text-gray-600" />
                                </div>
                                <div className="lesson-info">
                                  <span className="lesson-number">{lessonIndex + 1}.</span>
                                  <span className="lesson-title">{lesson.title}</span>
                                </div>
                                <div className="lesson-meta">
                                  <span className={`lesson-type-badge type-${lesson.type.toLowerCase()}`}>
                                    {lesson.type}
                                  </span>
                                  {lesson.duration && (
                                    <span className="lesson-duration">
                                      <Clock className="w-3.5 h-3.5" />
                                      {lesson.duration} min
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar - Course Stats */}
        <div className="section-sidebar">
          <div className="stats-card">
            <h3 className="stats-card-title">Course Highlights</h3>
            <div className="stats-list">
              <div className="stat-item">
                <div className="stat-icon bg-teal-100">
                  <Target className="w-5 h-5 text-teal-600" />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Skill Level</div>
                  <div className="stat-value">{course.level}</div>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon bg-purple-100">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Students Enrolled</div>
                  <div className="stat-value">{enrollmentCount}</div>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon bg-amber-100">
                  <Award className="w-5 h-5 text-amber-600" />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Certificate</div>
                  <div className="stat-value">Included</div>
                </div>
              </div>
            </div>
          </div>

          {/* Educator Card */}
          <div className="educator-card">
            <h3 className="educator-card-title">Your Educator</h3>
            <div className="educator-info">
              <div className="educator-avatar">
                <User className="w-8 h-8 text-teal-600" />
              </div>
              <div className="educator-details">
                <h4 className="educator-name">{course.Creator?.fullName || 'Educator'}</h4>
                <p className="educator-title">Course Instructor</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseDetail;
